import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { virtualTourKeys, type TourReclaim } from './virtual-tours.query';

/**
 * Mutation hooks for the Virtual Tours admin surface — write endpoints under
 * `POST /api/admin/virtual-tours/*`.
 *
 * Every endpoint here sends a JSON body, so its path must be present in the
 * proxy's `JSON_BODY_PAYLOAD_ENDPOINTS` allowlist (src/server/lib/utils.ts) or
 * the proxy flattens the body to FormData and `reason`/`full_rescan` are lost.
 */

const BASE = '/api/admin/virtual-tours';

const useInvalidate = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: virtualTourKeys.all });
};

/** Kick off a full reconcile sweep. `POST /reconcile` — `{reason, full_rescan?}`. 202 `{queued:true}`. */
export const useReconcileVirtualToursMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { reason: string; full_rescan?: boolean }) =>
        request<{ queued: boolean }>(`${BASE}/reconcile`, 'POST', {
          reason: vars.reason,
          ...(vars.full_rescan ? { full_rescan: true } : {}),
        }),
      'Failed to queue a reconcile'
    ),
    onSuccess: invalidate,
  });
};

/** Re-sync a single tour. `POST /<vtid>/resync` — `{reason}`. 409 when the listing's plan has no tour allowance. */
export const useResyncVirtualTourMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { vtid: number; reason: string }) =>
        request<{ result: any }>(`${BASE}/${vars.vtid}/resync`, 'POST', { reason: vars.reason }),
      'Failed to re-sync this tour'
    ),
    onSuccess: invalidate,
  });
};

/** Re-sync by listing instead of tour id. `POST /listings/<eid>/resync` — `{reason}`. */
export const useResyncListingTourMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; reason: string }) =>
        request<{ result: any }>(`${BASE}/listings/${vars.eid}/resync`, 'POST', { reason: vars.reason }),
      'Failed to re-sync this listing'
    ),
    onSuccess: invalidate,
  });
};

// ─── Write actions (link / move / unlink / archive / reactivate / visibility / rename) ───

/** Tour-or-model target: use the vtid forms when a tour row exists, else `/models/<model_id>/…`. */
export interface TourTarget {
  vtid?: number | null;
  model_id?: string | null;
}

const targetPath = (t: TourTarget, action: string): string =>
  t.vtid != null ? `${BASE}/${t.vtid}/${action}` : `${BASE}/models/${t.model_id}/${action}`;

export interface TourWriteResult {
  result: Record<string, any>;
}

/** Link a Matterport model to a listing. `POST /link`. 409 quota_blocked / already_linked. */
export const useLinkVirtualTourMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { model_id: string; eid: number; reason: string; bypass_quota?: boolean }) =>
        request<TourWriteResult>(`${BASE}/link`, 'POST', {
          model_id: vars.model_id,
          eid: vars.eid,
          reason: vars.reason,
          ...(vars.bypass_quota ? { bypass_quota: true } : {}),
        }),
      'Failed to link this model'
    ),
    onSuccess: invalidate,
  });
};

/** Move a tour to another listing. `POST /<vtid>/move`. */
export const useMoveVirtualTourMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { vtid: number; eid: number; reason: string; bypass_quota?: boolean }) =>
        request<TourWriteResult>(`${BASE}/${vars.vtid}/move`, 'POST', {
          eid: vars.eid,
          reason: vars.reason,
          ...(vars.bypass_quota ? { bypass_quota: true } : {}),
        }),
      'Failed to move this tour'
    ),
    onSuccess: invalidate,
  });
};

export const useUnlinkVirtualTourMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { vtid: number; reason: string }) =>
        request<TourWriteResult>(`${BASE}/${vars.vtid}/unlink`, 'POST', { reason: vars.reason }),
      'Failed to unlink this tour'
    ),
    onSuccess: invalidate,
  });
};

/** Archive. 409 `listing_live` -> resend with `force: true` once the admin confirms. */
export const useArchiveVirtualTourMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: TourTarget & { reason: string; force?: boolean }) =>
        request<TourWriteResult>(targetPath(vars, 'archive'), 'POST', {
          reason: vars.reason,
          ...(vars.force ? { force: true } : {}),
        }),
      'Failed to archive this tour'
    ),
    onSuccess: invalidate,
  });
};

/** Reactivate. 409 `capacity_full` when no free space. */
export const useReactivateVirtualTourMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: TourTarget & { reason: string }) =>
        request<TourWriteResult>(targetPath(vars, 'reactivate'), 'POST', { reason: vars.reason }),
      'Failed to reactivate this tour'
    ),
    onSuccess: invalidate,
  });
};

export type TourVisibility = 'private' | 'public' | 'unlisted';
export type AddressVisibility = 'full' | 'partial' | 'none';

export const useSetVisibilityVirtualTourMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: TourTarget & { visibility: TourVisibility; reason: string; force?: boolean }) =>
        request<TourWriteResult>(targetPath(vars, 'visibility'), 'POST', {
          visibility: vars.visibility,
          reason: vars.reason,
          ...(vars.force ? { force: true } : {}),
        }),
      'Failed to change visibility'
    ),
    onSuccess: invalidate,
  });
};

export const useSetAddressVisibilityMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { vtid: number; visibility: AddressVisibility; reason: string }) =>
        request<TourWriteResult>(`${BASE}/${vars.vtid}/address-visibility`, 'POST', {
          visibility: vars.visibility,
          reason: vars.reason,
        }),
      'Failed to change address visibility'
    ),
    onSuccess: invalidate,
  });
};

/** Rename / re-describe. `PATCH /<vtid>/details`. */
export const useRenameVirtualTourMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { vtid: number; name?: string; description?: string; reason: string }) =>
        request<TourWriteResult>(`${BASE}/${vars.vtid}/details`, 'PATCH', {
          ...(vars.name !== undefined ? { name: vars.name } : {}),
          ...(vars.description !== undefined ? { description: vars.description } : {}),
          reason: vars.reason,
        }),
      'Failed to update tour details'
    ),
    onSuccess: invalidate,
  });
};

/** Player settings. `PATCH /<vtid>/embed-options` — a null value clears that key. */
export const useSetEmbedOptionsMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { vtid: number; options: Record<string, string | null>; reason: string }) =>
        request<TourWriteResult>(`${BASE}/${vars.vtid}/embed-options`, 'PATCH', {
          options: vars.options,
          reason: vars.reason,
        }),
      'Failed to save player settings'
    ),
    onSuccess: invalidate,
  });
};

/** Queue a media import. `POST /<vtid>/media/import`. 409 quota_blocked / not_linked, 400 unsupported. */
export const useImportTourMediaMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { vtid: number; snapshot_ids: string[]; floorplan_ids: string[]; reason: string; bypass_quota?: boolean }) =>
        request<TourWriteResult>(`${BASE}/${vars.vtid}/media/import`, 'POST', {
          snapshot_ids: vars.snapshot_ids,
          floorplan_ids: vars.floorplan_ids,
          reason: vars.reason,
          ...(vars.bypass_quota ? { bypass_quota: true } : {}),
        }),
      'Failed to queue the import'
    ),
    onSuccess: invalidate,
  });
};

/** Order an add-on bundle (paid by FeelyFeely). `POST /<vtid>/addons/order`. 409 budget_reached. */
export const useOrderTourAddonMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { vtid: number; bundle_id: string; reason: string }) =>
        request<TourWriteResult>(`${BASE}/${vars.vtid}/addons/order`, 'POST', {
          bundle_id: vars.bundle_id,
          reason: vars.reason,
        }),
      'Failed to order this add-on'
    ),
    onSuccess: invalidate,
  });
};

/** Run slot reclaim. `POST /reclaim/run` — starts grace clocks, archives tours already past it. */
export const useRunTourReclaimMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { reason: string }) => request<{ result: TourReclaim }>(`${BASE}/reclaim/run`, 'POST', { reason: vars.reason }),
      'Failed to run slot reclaim'
    ),
    onSuccess: invalidate,
  });
};
